import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { userService } from "../services/userService";
import { getStoredToken } from "../utils/tokenUtils";

const ThemeContext = createContext();

// Default/ Slack preset colors - include primary palette + background vars (sidebar, content, tables)
const SLACK_THEME_PRESETS = {
  aubergine: {
    systemNavigation: "#4A154B",
    selectedItems: "#7D95EB",
    presence: "#228B07",
    notifications: "#2840A9",
    primary600: "#4A154B",
    primary100: "#f3e8f4",
    primary700: "#3d1240",
    linkColor: "#4A154B",
    bgGray: "#e8e0e9",
    bgSecondary: "#f5f3f6",
    bgTertiary: "#ede5ee",
  },
  clementine: {
    systemNavigation: "#C75D2C",
    selectedItems: "#E9A733",
    presence: "#228B07",
    notifications: "#2840A9",
    primary600: "#C75D2C",
    primary100: "#fdf0eb",
    primary700: "#a04a23",
    linkColor: "#C75D2C",
    bgGray: "#f9ebe5",
    bgSecondary: "#faf5f2",
    bgTertiary: "#f5e8e2",
  },
  banana: {
    systemNavigation: "#E9A733",
    selectedItems: "#F4D03F",
    presence: "#228B07",
    notifications: "#2840A9",
    primary600: "#E9A733",
    primary100: "#fef9eb",
    primary700: "#c7881f",
    linkColor: "#E9A733",
    bgGray: "#faf5e8",
    bgSecondary: "#fcfaf5",
    bgTertiary: "#f8f0dc",
  },
  jade: {
    systemNavigation: "#2C5530",
    selectedItems: "#4A154B",
    presence: "#228B07",
    notifications: "#2840A9",
    primary600: "#2C5530",
    primary100: "#e8f0e9",
    primary700: "#1f3d22",
    linkColor: "#2C5530",
    bgGray: "#e2ebe3",
    bgSecondary: "#f2f6f3",
    bgTertiary: "#dce8dd",
  },
  lagoon: {
    systemNavigation: "#0E7664",
    selectedItems: "#7D95EB",
    presence: "#228B07",
    notifications: "#2840A9",
    primary600: "#0E7664",
    primary100: "#e0f2ef",
    primary700: "#0a5548",
    linkColor: "#0E7664",
    bgGray: "#dcefeb",
    bgSecondary: "#f0f8f7",
    bgTertiary: "#d4eae6",
  },
  barbra: {
    systemNavigation: "#8B1538",
    selectedItems: "#C75D2C",
    presence: "#228B07",
    notifications: "#2840A9",
    primary600: "#8B1538",
    primary100: "#fce8ec",
    primary700: "#6b102b",
    linkColor: "#8B1538",
    bgGray: "#f5e4e8",
    bgSecondary: "#faf2f4",
    bgTertiary: "#eddce0",
  },
  gray: {
    systemNavigation: "#616061",
    selectedItems: "#8B1538",
    presence: "#228B07",
    notifications: "#2840A9",
    primary600: "#616061",
    primary100: "#f0efef",
    primary700: "#4a4949",
    linkColor: "#616061",
    bgGray: "#e8e7e8",
    bgSecondary: "#f5f4f5",
    bgTertiary: "#e0dfe0",
  },
  mood_indigo: {
    systemNavigation: "#3D2C6B",
    selectedItems: "#7D95EB",
    presence: "#228B07",
    notifications: "#2840A9",
    primary600: "#3D2C6B",
    primary100: "#ebe8f3",
    primary700: "#2d2050",
    linkColor: "#3D2C6B",
    bgGray: "#e5e2ed",
    bgSecondary: "#f4f2f8",
    bgTertiary: "#ddd8e8",
  },
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

function setPrimaryPalette(
  root,
  primary600,
  primary100,
  primary700,
  linkColor,
) {
  if (primary600) root.style.setProperty("--color-primary-600", primary600);
  if (primary100) root.style.setProperty("--color-primary-100", primary100);
  if (primary700) root.style.setProperty("--color-primary-700", primary700);
  if (linkColor) root.style.setProperty("--link-color", linkColor);
}

function clearPrimaryPaletteOverrides(root) {
  root.style.removeProperty("--color-primary-600");
  root.style.removeProperty("--color-primary-100");
  root.style.removeProperty("--color-primary-700");
  root.style.removeProperty("--link-color");
  root.style.removeProperty("--sidebar-color");
  root.style.removeProperty("--selected-item-color");
  root.style.removeProperty("--presence-color");
  root.style.removeProperty("--notifications-color");
  root.style.removeProperty("--color-bg-gray");
  root.style.removeProperty("--color-bg-secondary");
  root.style.removeProperty("--color-bg-tertiary");
  root.style.removeProperty("--color-bg-hover");
}

function applyThemeVariables(themeType, themeConfig) {
  const root = document.documentElement;
  if (
    themeType === "custom" &&
    themeConfig &&
    typeof themeConfig === "object"
  ) {
    root.style.setProperty(
      "--sidebar-color",
      themeConfig.systemNavigation || "",
    );
    root.style.setProperty(
      "--selected-item-color",
      themeConfig.selectedItems || "",
    );
    root.style.setProperty("--presence-color", themeConfig.presence || "");
    root.style.setProperty(
      "--notifications-color",
      themeConfig.notifications || "",
    );
    const main =
      themeConfig.systemNavigation || themeConfig.selectedItems || "#6D5EF3";
    root.style.setProperty("--color-primary-600", main);
    root.style.setProperty("--link-color", main);
    root.style.setProperty("--color-primary-700", main);
    const light = main.replace(
      /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i,
      (_, rr, gg, bb) =>
        `rgba(${parseInt(rr, 16)}, ${parseInt(gg, 16)}, ${parseInt(bb, 16)}, 0.15)`,
    );
    root.style.setProperty("--color-primary-100", light);
    const sidebarBg = main.replace(
      /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i,
      (_, rr, gg, bb) =>
        `rgba(${parseInt(rr, 16)}, ${parseInt(gg, 16)}, ${parseInt(bb, 16)}, 0.08)`,
    );
    const contentBg = main.replace(
      /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i,
      (_, rr, gg, bb) =>
        `rgba(${parseInt(rr, 16)}, ${parseInt(gg, 16)}, ${parseInt(bb, 16)}, 0.04)`,
    );
    root.style.setProperty("--color-bg-gray", sidebarBg);
    root.style.setProperty("--color-bg-secondary", contentBg);
    root.style.setProperty("--color-bg-tertiary", light);
    root.style.setProperty("--color-bg-hover", light);
    return;
  }
  if (
    themeType === "slack" &&
    themeConfig?.slackThemeId &&
    SLACK_THEME_PRESETS[themeConfig.slackThemeId]
  ) {
    const p = SLACK_THEME_PRESETS[themeConfig.slackThemeId];
    root.style.setProperty("--sidebar-color", p.systemNavigation);
    root.style.setProperty("--selected-item-color", p.selectedItems);
    root.style.setProperty("--presence-color", p.presence);
    root.style.setProperty("--notifications-color", p.notifications);
    setPrimaryPalette(
      root,
      p.primary600,
      p.primary100,
      p.primary700,
      p.linkColor,
    );
    if (p.bgGray) root.style.setProperty("--color-bg-gray", p.bgGray);
    if (p.bgSecondary)
      root.style.setProperty("--color-bg-secondary", p.bgSecondary);
    if (p.bgTertiary)
      root.style.setProperty("--color-bg-tertiary", p.bgTertiary);
    if (p.primary100) root.style.setProperty("--color-bg-hover", p.primary100);
    return;
  }
  // Default theme: white + purple (remove overrides so :root/.dark values apply)
  clearPrimaryPaletteOverrides(root);
}

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("theme") || "LIGHT";
  });
  const [isLoading, setIsLoading] = useState(false);

  const getEffectiveTheme = useCallback((themePreference) => {
    if (themePreference === "SYSTEM") {
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      return prefersDark ? "DARK" : "LIGHT";
    }
    return themePreference;
  }, []);

  const applyTheme = useCallback(
    (themePreference) => {
      const effectiveTheme = getEffectiveTheme(themePreference);
      if (effectiveTheme === "DARK") {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
      localStorage.setItem("theme", themePreference);
    },
    [getEffectiveTheme],
  );

  const updateTheme = useCallback(
    async (newTheme) => {
      if (!["LIGHT", "DARK", "SYSTEM"].includes(newTheme)) {
        console.error("Invalid theme:", newTheme);
        return;
      }
      setIsLoading(true);
      try {
        await userService.updateThemePreference(newTheme);
        setTheme(newTheme);
        applyTheme(newTheme);
      } catch (error) {
        console.error("Error updating theme:", error);
        setTheme(newTheme);
        applyTheme(newTheme);
      } finally {
        setIsLoading(false);
      }
    },
    [applyTheme],
  );

  const refreshTheme = useCallback(async () => {
    try {
      const token = getStoredToken();
      if (!token) return;
      const res = await userService.getTheme();
      if (res?.success && res?.data) {
        const d = res.data;
        setTheme(d.themeMode || "LIGHT");
        applyTheme(d.themeMode || "LIGHT");
        applyThemeVariables(d.themeType || "slack", d.themeConfig);
      }
    } catch (e) {
      console.error("Error refreshing theme:", e);
    }
  }, [applyTheme]);

  useEffect(() => {
    const loadThemeFromBackend = async () => {
      try {
        const token = getStoredToken();
        if (!token) {
          applyTheme(theme);
          return;
        }
        const response = await userService.getTheme();
        if (response?.success && response?.data) {
          const d = response.data;
          const mode = d.themeMode || "LIGHT";
          setTheme(mode);
          applyTheme(mode);
          applyThemeVariables(d.themeType || "slack", d.themeConfig);
        } else {
          const profileRes = await userService.getProfile();
          if (profileRes?.success && profileRes?.data?.theme_preference) {
            const backendTheme = profileRes.data.theme_preference;
            setTheme(backendTheme);
            applyTheme(backendTheme);
          } else {
            applyTheme(theme);
          }
        }
      } catch (error) {
        console.error("Error loading theme from backend:", error);
        applyTheme(theme);
      }
    };
    const token = getStoredToken();
    if (token) {
      loadThemeFromBackend();
    } else {
      applyTheme(theme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  useEffect(() => {
    if (theme !== "SYSTEM") return;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      if (localStorage.getItem("theme") === "SYSTEM") {
        applyTheme("SYSTEM");
      }
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme, applyTheme]);

  const value = {
    theme,
    effectiveTheme: getEffectiveTheme(theme),
    updateTheme,
    refreshTheme,
    isLoading,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
